import type {RootState} from '@/store';
import type {ItemObject} from '@PosterWhiteboard/items/item/item.types';
import {ITEM_TYPE, MIN_ITEM_DURATION} from '@PosterWhiteboard/items/item/item.types';
import {getItemPageEndTime} from '@PosterWhiteboard/items/item/item.library';
import {getPlaybackDuration} from '@Libraries/audio-player/audio-player.library';
import {getCurrentPageTime} from '@Components/poster-editor/poster-editor-reducer';
import {SHOW_ITEM_TIMELINE_TRACK} from '../timeline.types';
import {getSelectedAudioTrackIds} from './timeline-group-selection';

interface SplittableTimelineBars {
  itemUids: string[];
  audioUids: string[];
}

const isSpanSplittable = (startTime: number, endTime: number, splitTime: number): boolean => {
  return splitTime - startTime >= MIN_ITEM_DURATION && endTime - splitTime >= MIN_ITEM_DURATION;
};

/** The background visible at `time` — backgrounds tile the page as a sequence of half-open [start, end) spans, so at most one contains it. */
const findBackgroundAtTime = (itemsHashMap: Record<string, ItemObject>, time: number): ItemObject | undefined => {
  return Object.values(itemsHashMap).find((item) => {
    return item.isBackground && item.onPageStartTime <= time && time < getItemPageEndTime(item);
  });
};

/**
 * The bars a split at `splitTime` would cut: canvas items and backgrounds from the
 * fabric selection, audio clips from the audio selection — each only when the
 * playhead leaves at least MIN_ITEM_DURATION on both sides. With NO selection at
 * all, the split targets the background visible at `splitTime`. Transcripts and
 * slideshows never split (a slideshow's span is derived from its slides, so a
 * generic span split can't cut it). Non-background item bars are gated by
 * SHOW_ITEM_TIMELINE_TRACK: without the item track, fitItemsToVisualDuration resets
 * both halves to the full page span, leaving two identical overlapping copies.
 */
export const getSplittableTimelineBars = (state: RootState, splitTime: number): SplittableTimelineBars => {
  const splittableBars: SplittableTimelineBars = {itemUids: [], audioUids: []};
  const currentPageId = state.posterEditor.currentPageId;
  const currentPage = state.posterEditor.posterObject?.pages?.pagesHashMap?.[currentPageId];
  if (!currentPage) {
    return splittableBars;
  }

  const activeItemIds = state.posterEditor.activeItemIds[currentPageId] ?? [];
  const selectedAudioIds = getSelectedAudioTrackIds(state);

  if (activeItemIds.length === 0 && selectedAudioIds.length === 0) {
    const background = findBackgroundAtTime(currentPage.items.itemsHashMap, splitTime);
    if (background && isSpanSplittable(background.onPageStartTime, getItemPageEndTime(background), splitTime)) {
      splittableBars.itemUids.push(background.uid);
    }
    return splittableBars;
  }

  for (const uid of activeItemIds) {
    const itemObject = currentPage.items.itemsHashMap[uid];
    if (!itemObject || itemObject.gitype === ITEM_TYPE.TRANSCRIPT || itemObject.gitype === ITEM_TYPE.SLIDESHOW) {
      continue;
    }
    if (!itemObject.isBackground && !SHOW_ITEM_TIMELINE_TRACK) {
      continue;
    }
    if (isSpanSplittable(itemObject.onPageStartTime, getItemPageEndTime(itemObject), splitTime)) {
      splittableBars.itemUids.push(uid);
    }
  }

  for (const uid of selectedAudioIds) {
    const audioObject = currentPage.audioClips.audioItemsHashMap[uid];
    if (!audioObject) {
      continue;
    }
    const startTime = audioObject.onPageStartTime;
    if (isSpanSplittable(startTime, startTime + getPlaybackDuration(audioObject.audioPlayer), splitTime)) {
      splittableBars.audioUids.push(uid);
    }
  }

  return splittableBars;
};

/** Whether a split at `splitTime` would cut at least one bar — a selected one, or with no selection the background under the playhead. */
export const canSplitTimelineSelection = (state: RootState, splitTime: number): boolean => {
  const {itemUids, audioUids} = getSplittableTimelineBars(state, splitTime);
  return itemUids.length + audioUids.length > 0;
};

/**
 * Splits every splittable selected bar — canvas items, backgrounds, and audio clips —
 * at the current playhead, in ONE history entry. Bars the playhead misses stay
 * untouched; the selection stays on the left halves (originals keep their uids).
 */
export const splitTimelineSelection = async (): Promise<void> => {
  const page = window.posterEditor?.whiteboard?.getCurrentPage();
  if (!page) {
    return;
  }
  const state = window.PMW.redux.store.getState();
  const splitTime = getCurrentPageTime(state);
  await page.splitTimelineBars({...getSplittableTimelineBars(state, splitTime), splitTime});
};
